import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSidebar } from '../store/slices/uiSlice';
import { selectIsAuthenticated, selectCurrentUser, logout } from '../store/slices/authSlice';
import { setSearch, setSort, setBoard, setTag, resetFilters, selectFilters } from '../store/slices/filtersSlice';
import { selectAllPins } from '../store/slices/pinsSlice';
import { useGetBoardsQuery } from '../store/slices/apiSlice';
import NotificationsPanel from './NotificationsPanel';
import TagModal from './TagModal';

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(selectCurrentUser);
  const filters = useSelector(selectFilters);
  const allPins = useSelector(selectAllPins);
  const { data: boards = [], isLoading: boardsLoading } = useGetBoardsQuery();
  
  const [searchValue, setSearchValue] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [showTagModal, setShowTagModal] = useState(false);
  
  const searchTimeout = useRef(null);
  const searchPanelRef = useRef(null);

  // Show filters only on home page
  const showFilters = location.pathname === '/';

  // Generate trending searches from most common categories and tags
  useEffect(() => {
    if (allPins.length > 0) {
      const categoryCount = {};
      const tagCount = {};
      
      allPins.forEach(pin => {
        if (pin.category) {
          categoryCount[pin.category] = (categoryCount[pin.category] || 0) + 1;
        }
        if (pin.tags) {
          pin.tags.forEach(tag => {
            tagCount[tag] = (tagCount[tag] || 0) + 1;
          });
        }
      });
      
      // Get top 5 categories and top 5 tags
      const topCategories = Object.entries(categoryCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name]) => name);
      
      const topTags = Object.entries(tagCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name]) => name);
      
      // Combine and shuffle
      const trending = [...topCategories, ...topTags]
        .sort(() => Math.random() - 0.5)
        .slice(0, 8);
      
      setTrendingSearches(trending);
    }
  }, [allPins]);

  // Update search value when filters change
  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  // Handle search input with debounce
  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Debounce search
    searchTimeout.current = setTimeout(() => {
      dispatch(setSearch(value));
    }, 300);

    // Generate suggestions only if value has at least 1 character
    if (value.trim().length >= 1) {
      generateSuggestions(value.trim().toLowerCase());
    } else {
      setSearchSuggestions([]);
      // Show trending when empty
      if (showSearchPanel) {
        setShowSearchPanel(true);
      }
    }
  };

  // Generate search suggestions
  const generateSuggestions = (keyword) => {
    const suggestions = new Set();
    
    allPins.forEach(pin => {
      if (pin.category && pin.category.toLowerCase().includes(keyword)) {
        suggestions.add(pin.category);
      }
      if (pin.tags) {
        pin.tags.forEach(tag => {
          if (tag.toLowerCase().includes(keyword)) {
            suggestions.add(tag);
          }
        });
      }
    });

    const suggestionArray = Array.from(suggestions).slice(0, 8);
    setSearchSuggestions(suggestionArray);
    
    // Show panel if we have suggestions or if showing trending
    if (suggestionArray.length > 0 || keyword.length === 0) {
      setShowSearchPanel(true);
    } else {
      setShowSearchPanel(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setSearchValue(suggestion);
    dispatch(setSearch(suggestion));
    setShowSearchPanel(false);
  };

  // Close search panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchPanelRef.current && !searchPanelRef.current.contains(e.target)) {
        setShowSearchPanel(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleClearFilters = () => {
    dispatch(resetFilters());
    setSearchValue('');
  };

  return (
    <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 shadow-sm">
      {/* Main Header Row */}
      <div className="h-[60px] lg:h-[80px] flex items-center gap-2 lg:gap-4 px-4 lg:px-6">
        {/* Mobile Menu Button */}
        <button
          onClick={() => dispatch(toggleSidebar())}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all hover:scale-110 hover:rotate-180 duration-300"
        >
          <span className="material-symbols-rounded text-gray-700 dark:text-gray-300">menu</span>
        </button>

        {/* Mobile Logo */}
        <a href="/" className="lg:hidden flex items-center">
          <img src="https://res.cloudinary.com/ds52btbjy/image/upload/v1772812120/logo_full_hcwxaa.png" alt="Logo" className="w-8 h-8 object-contain" />
        </a>

        {/* Search Bar */}
        <div className="flex-1 relative" ref={searchPanelRef}>
          <div className="relative flex items-center bg-gray-100 dark:bg-gray-700 rounded-full h-12 transition-colors focus-within:bg-gray-200 dark:focus-within:bg-gray-600">
            <span className="material-symbols-rounded absolute left-4 text-gray-500">search</span>
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchInput}
              onFocus={() => setShowSearchPanel(true)}
              placeholder="Tìm kiếm..."
              className="w-full bg-transparent border-none pl-12 pr-12 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
            />
            {searchValue && (
              <button
                onClick={() => {
                  setSearchValue('');
                  dispatch(setSearch(''));
                  setSearchSuggestions([]);
                }}
                className="absolute right-4 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                <span className="material-symbols-rounded text-gray-500 text-[18px]">close</span>
              </button>
            )}
          </div>

          {/* Search Suggestions Panel */}
          {showSearchPanel && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-h-[70vh] sm:max-h-96 overflow-y-auto z-50 animate-scale-in">
              {/* Show suggestions if searching */}
              {searchValue.trim() && searchSuggestions.length > 0 ? (
                <>
                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 uppercase">Gợi ý tìm kiếm</p>
                  </div>
                  {searchSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-3 border-b border-gray-100 dark:border-gray-700 last:border-0 transition-all active:bg-gray-100 dark:active:bg-gray-600 hover:translate-x-1 animate-slide-in-left stagger-${Math.min(index + 1, 6)}`}
                    >
                      <span className="material-symbols-rounded text-gray-400 text-[20px]">search</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">{suggestion}</span>
                    </div>
                  ))}
                </>
              ) : searchValue.trim() && searchSuggestions.length === 0 ? (
                <div className="px-4 py-8 text-center animate-fade-in">
                  <span className="material-symbols-rounded text-gray-300 text-[48px] mb-2 block animate-wiggle">search_off</span>
                  <p className="text-gray-500 text-sm">Không tìm thấy kết quả</p>
                </div>
              ) : (
                /* Show trending searches when not searching */
                <>
                  {trendingSearches.length > 0 && (
                    <>
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                        <span className="material-symbols-rounded text-orange-500 text-[20px] animate-pulse-slow">whatshot</span>
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Tìm kiếm phổ biến</p>
                      </div>
                      <div className="p-3 flex flex-wrap gap-2">
                        {trendingSearches.map((term, index) => (
                          <button
                            key={index}
                            onClick={() => handleSuggestionClick(term)}
                            className={`px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 transition-all active:scale-95 hover:scale-105 animate-bounce-in stagger-${Math.min(index + 1, 6)}`}
                          >
                            {term}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  
                  {/* Quick Categories */}
                  <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Danh mục phổ biến</p>
                    <div className="grid grid-cols-2 gap-2">
                      {boardsLoading ? (
                        // Loading skeleton
                        Array(6).fill(0).map((_, index) => (
                          <div key={index} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse h-9" />
                        ))
                      ) : (
                        boards.slice(0, 6).map((board, index) => (
                          <button
                            key={board._id}
                            onClick={() => {
                              dispatch(setBoard(board._id));
                              setShowSearchPanel(false);
                              if (location.pathname !== '/') {
                                navigate('/');
                              }
                            }}
                            className={`px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 dark:hover:from-blue-900/30 dark:hover:to-purple-900/30 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 transition-all active:scale-95 hover:scale-105 text-left animate-fade-in-up stagger-${Math.min(index + 1, 6)}`}
                          >
                            <span className="text-[16px] mr-1 align-middle">{board.icon || '📌'}</span>
                            {board.name}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {isAuthenticated ? (
          <>
            {/* Notifications */}
            <NotificationsPanel />

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="w-9 h-9 lg:w-10 lg:h-10 rounded-full overflow-hidden border-2 border-transparent hover:border-primary-500 transition-all hover:scale-110 hover:rotate-6 duration-300"
              >
                <img
                  src={currentUser?.avatar || 'https://api.dicebear.com/7.x/notionists/svg?seed=guest'}
                  alt={currentUser?.displayName}
                  className="w-full h-full object-cover bg-gray-100"
                />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden z-50 animate-scale-in">
                  <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <img
                        src={currentUser?.avatar || 'https://api.dicebear.com/7.x/notionists/svg?seed=guest'}
                        className="w-12 h-12 rounded-full bg-gray-100"
                        alt={currentUser?.displayName}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 dark:text-gray-100 truncate">{currentUser?.displayName}</p>
                        <p className="text-sm text-gray-500 truncate">{currentUser?.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-left hover:translate-x-1"
                    >
                      <span className="material-symbols-rounded text-gray-600 dark:text-gray-400">person</span>
                      <span className="text-gray-800 dark:text-gray-200">Trang cá nhân</span>
                    </button>
                    {currentUser?.role === 'admin' && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all text-left hover:translate-x-1"
                      >
                        <span className="material-symbols-rounded text-purple-600">admin_panel_settings</span>
                        <span className="text-purple-600 font-semibold">Admin Dashboard</span>
                      </button>
                    )}
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 py-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all text-left hover:translate-x-1"
                    >
                      <span className="material-symbols-rounded text-red-600">logout</span>
                      <span className="text-red-600 font-medium">Đăng xuất</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 lg:px-6 py-2 rounded-full font-semibold transition-all hover:scale-105 shadow-md btn-ripple animate-gradient"
          >
            Đăng nhập
          </button>
        )}
      </div>

      {/* Filter Bar Row - Only on Home Page */}
      {showFilters && (
        <div className="px-2 lg:px-4 py-2 border-t border-gray-50 dark:border-gray-700 flex items-center gap-2 sm:gap-3 overflow-x-auto no-scrollbar animate-slide-down">
          {/* Sort Filter */}
          <div className="filter-group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100/50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 shrink-0 hover:shadow-md transition-all">
            <span className="material-symbols-rounded text-sm text-gray-500">sort</span>
            <select
              value={filters.sort}
              onChange={(e) => dispatch(setSort(e.target.value))}
              className="bg-transparent border-none text-xs font-semibold text-gray-700 dark:text-gray-300 focus:ring-0 cursor-pointer pr-1"
            >
              <option value="newest">Mới nhất</option>
              <option value="oldest">Cũ nhất</option>
              <option value="popular">Phổ biến</option>
            </select>
          </div>

          {/* Board Filter */}
          <div className="filter-group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100/50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 shrink-0 hover:shadow-md transition-all">
            <span className="material-symbols-rounded text-sm text-gray-500">dashboard</span>
            <select
              value={filters.board || ''}
              onChange={(e) => dispatch(setBoard(e.target.value))}
              className="bg-transparent border-none text-xs font-semibold text-gray-700 dark:text-gray-300 focus:ring-0 cursor-pointer min-w-[100px] max-w-[150px] pr-1"
              disabled={boardsLoading}
            >
              <option value="">Tất cả bảng</option>
              {boards && boards.length > 0 ? (
                boards.map(board => (
                  <option key={board._id} value={board._id}>
                    {board.icon || '📌'} {board.name}
                  </option>
                ))
              ) : (
                <option disabled>Đang tải...</option>
              )}
            </select>
          </div>

   

          {/* Active Filters Indicator */}
          {(filters.search || filters.board || filters.tag) && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-semibold shrink-0 animate-bounce-in">
              <span className="material-symbols-rounded text-sm animate-pulse-slow">filter_alt</span>
              <span>
                {[filters.search, filters.board, filters.tag].filter(Boolean).length} bộ lọc
              </span>
            </div>
          )}

          {/* Clear Filters */}
          <button
            onClick={handleClearFilters}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-all shrink-0 hover:scale-110 hover:rotate-180 duration-300"
            title="Xoá lọc"
          >
            <span className="material-symbols-rounded text-xl">restart_alt</span>
          </button>
        </div>
      )}

      {/* Tag Modal */}
      {showTagModal && (
        <TagModal
          onClose={() => setShowTagModal(false)}
          onSelectTag={(tag) => {
            dispatch(setTag(tag));
            setShowTagModal(false);
          }}
          selectedTag={filters.tag}
        />
      )}
    </header>
  );
}

export default Header;
